/**
 * 选中格子的样式面板。画布上单击 / Shift 拉选后，右侧属性栏顶上出现这一块，
 * 改的是选区里所有格子。以前只有加粗、灰底、对齐三样，做单据的人一定会要
 * 表头底色和单格字号 —— 这两样是竞品对比里明确落后的一项。
 */
import { Button, ColorPicker, InputNumber, Segmented, Space, Switch, Typography } from 'antd';
import type { CellProps, Rect } from '../../../contract/freeTable';
import { anchorAt } from '../../../contract/freeTable';
import type { FreeTableSection } from '../../../contract/types';

interface Props {
  section: FreeTableSection;
  rect: Rect;
  onPatch: (patch: Partial<CellProps>) => void;
}

const SWATCHES = ['#000000', '#c0392b', '#1f6feb', '#2d6a4f', '#7a5210', '#8e44ad', '#ffffff'];
const BG_SWATCHES = ['#f2f2f2', '#fde8e4', '#e6f0ff', '#e3f4ea', '#fff4d6', '#f0e6f6'];

export function CellStylePanel({ section, rect, onPatch }: Props) {
  const cell = anchorAt(section, rect.r0, rect.c0);
  if (!cell) return null;
  const many = rect.r1 > rect.r0 || rect.c1 > rect.c0;
  const label = (t: string) => (
    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
      {t}
    </Typography.Text>
  );
  return (
    <div
      style={{
        padding: '10px 12px',
        marginBottom: 12,
        borderRadius: 8,
        border: '1px solid var(--border)',
        background: 'var(--surface-2)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', marginBottom: 8 }}>
        单元格{many ? `（选中 ${(rect.r1 - rect.r0 + 1) * (rect.c1 - rect.c0 + 1)} 格）` : ''}
      </div>
      <Space size={[12, 8]} wrap align="start">
        <div>
          {label('对齐')}
          <br />
          <Segmented
            size="small"
            value={cell.align}
            options={[
              { label: '左', value: 'left' },
              { label: '中', value: 'center' },
              { label: '右', value: 'right' },
            ]}
            onChange={(v) => onPatch({ align: v as CellProps['align'] })}
          />
        </div>
        <div>
          {label('加粗')}
          <br />
          <Switch size="small" checked={cell.bold} onChange={(bold) => onPatch({ bold })} />
        </div>
        <div>
          {label('字号(px)')}
          <br />
          <InputNumber
            size="small"
            min={6}
            max={36}
            value={cell.fontSizePx ?? null}
            placeholder="跟表"
            style={{ width: 70 }}
            onChange={(v) => onPatch({ fontSizePx: v ?? null })}
          />
        </div>
        <div>
          {label('文字颜色')}
          <br />
          <ColorPicker
            size="small"
            value={cell.color ?? '#000000'}
            presets={[{ label: '常用', colors: SWATCHES }]}
            onChangeComplete={(c) => onPatch({ color: c.toHexString() })}
          />
        </div>
        <div>
          {label('背景色')}
          <br />
          <ColorPicker
            size="small"
            value={cell.bg ?? (cell.shaded ? '#f2f2f2' : '#ffffff')}
            presets={[{ label: '常用', colors: BG_SWATCHES }]}
            onChangeComplete={(c) => onPatch({ bg: c.toHexString(), shaded: false })}
          />
        </div>
        <div>
          {label('清除')}
          <br />
          <Button
            size="small"
            onClick={() => onPatch({ color: null, bg: null, fontSizePx: null, bold: false, shaded: false })}
          >
            恢复默认
          </Button>
        </div>
      </Space>
    </div>
  );
}
